/**
 * apiClient — the SPA's single transport module (API service boundary rule,
 * coding-standards §3).
 *
 * The ONLY file in web/ that touches fetch. Every backend call goes through
 * here. Same-origin only (ADR-000: nginx on the host terminates TLS and
 * proxies `/api/*` to either the auth gateway on 127.0.0.1:9102 for `/api/auth/*`
 * or the BFF on 127.0.0.1:8080 for `/api/agg/*` per ADR-002 D3).
 *
 * Boundary rules:
 *   - transport lives ONLY here (raw fetch is forbidden elsewhere);
 *   - route strings live ONLY inside src/api/ (the `endpoints` registry here and the
 *     per-service modules: authApi, dashboardApi, tpinApi); apiLayer.test.ts enforces it;
 *   - 401 from any backend call is treated as "session expired" and routed
 *     to the registered handler so the AuthGate can re-render the login
 *     page (QAS-3: the user is never silently stuck on a stale view).
 */

import { ApiError } from '@domain/types'

export interface ErrorEnvelope {
  readonly code: string
  readonly message: string
}

interface RequestOptions {
  readonly method?: 'GET' | 'POST'
  readonly body?: unknown
  readonly signal?: AbortSignal
  /** Extra headers (CSRF, idempotency keys, etc.). `Accept`/`Content-Type` are merged in. */
  readonly headers?: Record<string, string>
}

async function parseJson(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    // A non-JSON error body is itself a named failure — never swallowed.
    throw new ApiError(response.status, 'INVALID_RESPONSE', `Server returned a non-JSON body (HTTP ${response.status}).`)
  }
}

/**
 * Extracts the gateway's error envelope. The gateway contract (OpenAPI
 * components/schemas/ApiError) carries: timestamp, status, error, code,
 * message, correlationId. Older BFF envelopes use {error:{code,message}}.
 * Both shapes map to the same typed extraction without leaking either.
 */
function extractEnvelope(body: unknown, fallbackCode: string): ErrorEnvelope {
  if (body !== null && typeof body === 'object') {
    // Gateway shape (flat) — components/schemas/ApiError.
    const flat = body as { code?: unknown; message?: unknown }
    if (typeof flat.code === 'string' && typeof flat.message === 'string') {
      return { code: flat.code, message: flat.message }
    }
    // Legacy BFF shape ({error:{code,message}}) — accepted for transition.
    if ('error' in flat) {
      const nested = (flat as { error?: { code?: unknown; message?: unknown } }).error
      if (nested !== null && typeof nested === 'object' && typeof nested?.code === 'string' && typeof nested?.message === 'string') {
        return { code: nested.code, message: nested.message }
      }
    }
  }
  return { code: fallbackCode, message: `Unexpected error shape from server (HTTP ${fallbackCode === 'HTTP_401' ? '401' : 'error'}).` }
}

let unauthorizedHandler: (() => void) | null = null

/**
 * Register a single handler invoked on any 401 from a backend call (cookie
 * missing, session expired, server-revoked session). The AuthStore registers
 * its handler at module init so the rest of the SPA can stay decoupled from
 * the auth controller. Idempotent — last registration wins.
 */
export function setUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(options.headers ?? {}),
  }
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  // Forward the signal when one is provided — the browser's fetch expects
  // the browser's AbortSignal class, so structural checks suffice there.
  // jsdom's bundled fetch (vitest+jsdom) rejects Node's AbortController
  // signal with a TypeError ("Expected signal ... to be an instance of
  // AbortSignal") — we detect this specific failure once and retry without
  // the signal. Production behaviour is unchanged (no error path on the
  // happy path); tests work; signal-based cancellation still works in
  // the browser because the first attempt succeeds.
  const fetchOptions: RequestInit = {
    method,
    credentials: 'same-origin',
    headers,
    body: options.body === undefined ? null : JSON.stringify(options.body),
  }
  if (options.signal !== undefined) {
    fetchOptions.signal = options.signal
  }

  let response: Response
  try {
    response = await fetch(path, fetchOptions)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    // jsdom + Node-fetch mismatch — drop the signal and retry once.
    if (options.signal !== undefined && message.includes('Expected signal')) {
      // A separate options object: the one already handed to fetch must not
      // be mutated after dispatch, or the request that was sent can lose its
      // signal depending on when the runtime reads it.
      const retryOptions: RequestInit = { ...fetchOptions }
      delete retryOptions.signal
      try {
        response = await fetch(path, retryOptions)
      } catch (retryError) {
        if (retryError instanceof DOMException && retryError.name === 'AbortError') throw retryError
        throw new ApiError(0, 'UNREACHABLE', `Cannot reach the dashboard service — ${(retryError as Error).message}`)
      }
    } else {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      throw new ApiError(0, 'UNREACHABLE', `Cannot reach the dashboard service — ${message}`)
    }
  }

  if (response.status === 401 && unauthorizedHandler !== null) {
    // Notify AuthStore before throwing so the AuthGate can re-render on the
    // next tick. The handler is a no-op if the auth flow itself caused the
    // 401 (login attempts); it only matters for already-authenticated calls.
    unauthorizedHandler()
  }

  return response
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options)

  if (!response.ok) {
    const body = await parseJson(response)
    const envelope = extractEnvelope(body, `HTTP_${response.status}`)
    throw new ApiError(response.status, envelope.code, envelope.message)
  }

  return (await parseJson(response)) as T
}

/** A successful response body plus the one header the paged contract adds. */
export interface WireResponse {
  readonly body: unknown
  /** Value of `Cursor-Version`, or null when the server sent none (ADR-005 §15). */
  readonly cursorVersion: string | null
}

/**
 * The BFF error envelope of the TPIN family (ADR-005 §3.6): `{id, type, code,
 * message}` where `type` is the name (BadRequest, Unauthorized,
 * SourceUnavailable, PartialResult). Null when the body is not that shape.
 */
function extractTypedEnvelope(body: unknown): { type: string; message: string } | null {
  // `Object(x) === x` is true only for objects (and arrays); null and every
  // primitive fail it, so one test of one condition covers the whole guard.
  if (Object(body) !== body) return null
  const candidate = body as { type?: unknown; message?: unknown }
  if (typeof candidate.type === 'string' && typeof candidate.message === 'string') {
    return { type: candidate.type, message: candidate.message }
  }
  return null
}

/**
 * GET for the TPIN-first family: same transport, same 401 handling, but the
 * `Cursor-Version` header is returned and the typed error envelope name
 * becomes the ApiError code (so a 503 PartialResult stays distinguishable).
 */
export async function requestWire(path: string, signal?: AbortSignal): Promise<WireResponse> {
  const response = await send(path, { signal })
  const body = await parseJson(response)
  if (!response.ok) {
    const typed = extractTypedEnvelope(body)
    if (typed !== null) throw new ApiError(response.status, typed.type, typed.message)
    const envelope = extractEnvelope(body, `HTTP_${response.status}`)
    throw new ApiError(response.status, envelope.code, envelope.message)
  }
  return { body, cursorVersion: response.headers.get('Cursor-Version') }
}

/** Typed endpoint registry — one place where route strings exist. */
export const endpoints = {
  // Customer auth (ADR-003 D2/D8 — gateway on 127.0.0.1:9102 behind nginx /api/auth/*).
  authLogin: '/api/auth/login',
  authLogout: '/api/auth/logout',
  authMe: '/api/auth/me',
  // SIH-17 / DEF-B4-1 — capability probe (no auth, ops-only, ADR-003 §2.2 D2
  // table). The SPA's runtime detection of the customer auth gateway.
  customerGatewayHealthz: '/api/auth/healthz',
  // Employee auth (ADR-003 D8 — BFF on 127.0.0.1:9103 behind nginx /api/staff-auth/*;
  // the same host's nginx routes both prefixes — the SPA never calls one surface
  // through the other).
  staffAuthLogin: '/api/staff-auth/login',
  staffAuthVerify: '/api/staff-auth/verify',
  staffAuthLogout: '/api/staff-auth/logout',
  staffAuthMe: '/api/staff-auth/me',
  // Aggregation (proxy via BFF — data routes live under /api/agg/* per ADR-002 D3).
  deployments: '/api/agg/deployments',
  summary: (tinBranch: string, from?: string, to?: string) => {
    const params = new URLSearchParams()
    if (from !== undefined) params.set('from', from)
    if (to !== undefined) params.set('to', to)
    const qs = params.toString()
    return `/api/agg/deployments/${encodeURIComponent(tinBranch)}/summary${qs ? `?${qs}` : ''}`
  },
  invoices: (
    tinBranch: string,
    query: { status?: 'SYNCED' | 'FAILED' | 'PENDING'; from?: string; to?: string; cursor?: string; limit?: number },
  ) => {
    const params = new URLSearchParams()
    if (query.status !== undefined) params.set('status', query.status)
    if (query.from !== undefined) params.set('from', query.from)
    if (query.to !== undefined) params.set('to', query.to)
    if (query.cursor !== undefined) params.set('cursor', query.cursor)
    if (query.limit !== undefined) params.set('limit', String(query.limit))
    return `/api/agg/deployments/${encodeURIComponent(tinBranch)}/invoices?${params.toString()}`
  },
} as const

export function getDeployments(signal?: AbortSignal): Promise<unknown> {
  return request<unknown>(endpoints.deployments, { signal })
}

export function getSummary(tinBranch: string, from: string | undefined, to: string | undefined, signal?: AbortSignal): Promise<unknown> {
  return request<unknown>(endpoints.summary(tinBranch, from, to), { signal })
}

export function getInvoices(
  tinBranch: string,
  query: { status?: 'SYNCED' | 'FAILED' | 'PENDING'; from?: string; to?: string; cursor?: string; limit?: number },
  signal?: AbortSignal,
): Promise<unknown> {
  return request<unknown>(endpoints.invoices(tinBranch, query), { signal })
}

/**
 * Capability probe — the SPA's runtime detection of the customer auth
 * gateway (SIH-17 / DEF-B4-1). Returns the raw `Response` so the caller
 * (capabilityProbe.ts) can collapse every failure mode (non-2xx, abort,
 * network error) to a single `'absent'` verdict without going through the
 * standard `request()` pipeline (which would map non-2xx to typed ApiErrors
 * and trigger the unauthorized handler).
 *
 * The only raw fetch in the codebase lives in this file; this method is
 * the only place outside `request()` that touches the network. The probe
 * is intentionally minimal: GET /api/auth/healthz, same-origin credentials.
 *
 * Note: this method deliberately does NOT accept an AbortSignal. The probe
 * owns its timeout in capabilityProbe.ts via a Promise.race against a
 * setTimeout — that keeps the network call free of AbortSignal plumbing
 * (jsdom's fetch checks the signal against its own AbortSignal class
 * and rejects Node's, which makes signal-based cancellation unreliable
 * across environments). The caller can still cancel the outer Promise
 * because the consumer's setTimeout is independent.
 */
export async function probeCustomerGatewayHealth(): Promise<Response> {
  const fetchOptions: RequestInit = {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  }
  return fetch(endpoints.customerGatewayHealthz, fetchOptions)
}
